/**
 * static_merge — many small STATIC meshes on one material become ONE draw (2026-09-20).
 *
 * WHY: event and civic decor is built from primitives — a festival square is 20–30 posts, trestles, logs and ring
 * stones; a campaign's bunting is 22 meshes; a notice board 9 — each its own render object on a renderer whose city
 * frames are CPU-bound on per-object cost. Their materials are already shared by colour (`propMaterial`, `mat`,
 * `clothMat`), so everything on one material can be one geometry.
 *
 * THE PICTURE DOES NOT CHANGE: only OPAQUE, single-material, unskinned, un-instanced, childless meshes merge; each
 * geometry is baked through its own transform relative to `root`; grouping keeps the material, shadow flags, render
 * order and layers apart. A transparent mesh is never merged (per-object sorting is part of its look), nor a
 * mirrored one (the bake would flip its winding), nor a generated prop clone (`userData.sharedAsset` — its geometry
 * belongs to a master). Anything that does not fit is simply left as it was.
 *
 * CALL IT BEFORE THE FIRST RENDER of the subtree (at construction): the source geometries have no GPU copies yet
 * and are disposed on the spot. `skip` names what must stay its own object — an animated cloth, a turning emblem,
 * anything a module keeps a handle to.
 */
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export interface MergeReport { before: number; after: number; merged: number; groups: number }

const KEEP = ['position', 'normal', 'uv'] as const;
const _inv = new THREE.Matrix4();
const _rel = new THREE.Matrix4();

function mergeable(m: THREE.Mesh, root: THREE.Object3D): boolean {
  if (!m.isMesh || (m as unknown as THREE.SkinnedMesh).isSkinnedMesh || (m as unknown as THREE.InstancedMesh).isInstancedMesh || (m as unknown as { isBatchedMesh?: boolean }).isBatchedMesh) return false;
  if (m.children.length > 0 || Array.isArray(m.material) || !m.material || !m.geometry) return false;
  const mat = m.material as THREE.Material;
  // (geometry.groups are NOT a reason: Box/Cylinder carry per-face groups, and with a single material three ignores them.)
  if (mat.transparent || m.morphTargetInfluences !== undefined) return false;
  for (const name of KEEP) {
    // Plain float attributes only (the primitives): a cooked GLB's quantized / interleaved streams are left alone.
    const a = m.geometry.getAttribute(name) as THREE.BufferAttribute | undefined;
    if (!a || (a as unknown as { isInterleavedBufferAttribute?: boolean }).isInterleavedBufferAttribute || a.normalized || !(a.array instanceof Float32Array)) return false;
  }
  for (let o: THREE.Object3D | null = m; o && o !== root; o = o.parent) if (!o.visible || o.userData.sharedAsset === true) return false;
  return true;
}

/** Merge what can be merged under `root`. Returns the mesh counts before / after. */
export function mergeStaticMeshes(root: THREE.Object3D, skip?: (m: THREE.Mesh) => boolean): MergeReport {
  root.updateMatrixWorld(true);
  _inv.copy(root.matrixWorld).invert();
  const buckets = new Map<string, THREE.Mesh[]>();
  let before = 0;
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    before++;
    if (!mergeable(m, root) || skip?.(m)) return;
    if (_rel.multiplyMatrices(_inv, m.matrixWorld).determinant() <= 0) return; // mirrored / degenerate: the bake would flip it
    const key = `${(m.material as THREE.Material).uuid}|${m.castShadow ? 1 : 0}${m.receiveShadow ? 1 : 0}|${m.renderOrder}|${m.layers.mask}`;
    const list = buckets.get(key);
    if (list) list.push(m); else buckets.set(key, [m]);
  });
  let merged = 0, groups = 0;
  for (const list of buckets.values()) {
    if (list.length < 2) continue;
    const indexed = list.every((m) => m.geometry.index !== null);
    const parts = list.map((m) => {
      const src = indexed ? m.geometry : (m.geometry.index ? m.geometry.toNonIndexed() : m.geometry);
      const g = new THREE.BufferGeometry();
      for (const name of KEEP) g.setAttribute(name, (src.getAttribute(name) as THREE.BufferAttribute).clone());
      if (indexed && src.index) g.setIndex(src.index.clone());
      g.applyMatrix4(_rel.multiplyMatrices(_inv, m.matrixWorld));
      if (src !== m.geometry) src.dispose();
      return g;
    });
    const geo = mergeGeometries(parts, false);
    for (const p of parts) p.dispose();
    if (!geo) continue; // attribute layouts disagreed after all: leave this bucket as it was
    const first = list[0];
    const out = new THREE.Mesh(geo, first.material);
    out.name = `merged:${first.name || 'static'}×${list.length}`;
    out.castShadow = first.castShadow; out.receiveShadow = first.receiveShadow;
    out.renderOrder = first.renderOrder; out.layers.mask = first.layers.mask;
    out.matrixAutoUpdate = false; // identity under root, for ever
    root.add(out);
    for (const m of list) { m.parent?.remove(m); m.geometry.dispose(); }
    merged += list.length; groups++;
  }
  return { before, after: before - merged + groups, merged, groups };
}
